// Staleness rules for the pooled-dialer stream (card 472).
//
// The pool UI is a pure render of the last snapshot it received, so a snapshot
// that quietly stops refreshing is worse than no data: it keeps promising a
// specific "Up next" contact, keeps a wrap-up countdown running, and keeps the
// agent convinced they're in the queue — all from a view of the floor that no
// longer exists. Two real-world failure shapes feed it:
//
//  - The transport drops (agent's network flap): EventSource errors or the
//    ping watchdog trips, `live` flips false, and the frozen snapshot is
//    rendered indefinitely with only a small "reconnecting" badge changing.
//  - The transport half-works: pings keep arriving so the watchdog never
//    trips and `live` stays true, but no `pool` event lands. The fallback
//    poll is gated off while `live`, so nothing can ever heal the view.
//
// These helpers are pure so the rules are testable: when a quiet-but-pinging
// stream owes a refresh, whether a finished refresh may overwrite the
// snapshot, and when a snapshot is too old to present as the truth.

/** A live stream this quiet owes a snapshot refresh — pool state broadcasts
 * on every change and a working floor changes constantly, so a long `pool`
 * silence under a healthy ping stream means the view is frozen, whatever the
 * cause. */
export const POOL_EVENT_QUIET_REFRESH_MS = 30_000;

/** How old a dead-stream snapshot may grow before the UI must stop rendering
 * it as the current floor. Comfortably above one reconnect cycle (5s) plus the
 * fallback poll cadence (2s), so a TRANSPORT blip never flashes the lost
 * screen. A quiet-refresh escalation is different on purpose: the snapshot is
 * already far past this age when the stream is torn down, so the lost screen
 * shows immediately — the view was already minutes behind the floor. */
export const SNAPSHOT_STALE_UI_MS = 15_000;

/** Consecutive quiet-refresh failures before a nominally-live stream must be
 * treated as disconnected. One failure can be a blip; a second means two full
 * quiet windows have passed with pings arriving but neither push nor pull able
 * to move data — the saturated-uplink shape, where tiny SSE pings squeeze
 * through while every status fetch times out. */
export const QUIET_REFRESH_FAILURES_TO_DISCONNECT = 2;

/** How far a live snapshot may lag behind wall-clock time before the view may
 * show the "as of" watermark. Age alone is NOT the trigger: pool broadcasts
 * are change-driven with no heartbeat, so a healthy floor legitimately goes
 * this quiet during any lull (single agent mid-call, wrap-up, off-hours) —
 * the watermark also requires a proven pull failure. */
export const SNAPSHOT_WATERMARK_MS = 10_000;

/** Quiet-refresh failure telemetry cadence: the first failure of an episode
 * always reports; a long outage then reports at most this often. */
export const QUIET_REFRESH_REPORT_MIN_INTERVAL_MS = 60_000;

/**
 * On each server ping: does this stream owe itself a one-shot status refresh?
 * Pings prove the transport; a missing `pool` event this long proves the
 * subscription isn't delivering. One refresh at a time.
 */
export function shouldRefreshOnPing(
  nowMs: number,
  lastPoolEventAtMs: number,
  refreshInFlight: boolean
): boolean {
  if (refreshInFlight) return false;
  return nowMs - lastPoolEventAtMs > POOL_EVENT_QUIET_REFRESH_MS;
}

/**
 * A refresh that raced a real `pool` event loses: the pushed event is newer
 * by construction (the fetch snapshot was taken at request time).
 */
export function refreshResultApplies(
  lastPoolEventAtMs: number,
  refreshStartedAtMs: number
): boolean {
  return lastPoolEventAtMs <= refreshStartedAtMs;
}

/**
 * Too old to render as the current floor. Only ever true for a non-live
 * stream that HAS shown data — `updatedAtMs === 0` is the initial load, which
 * has its own empty states.
 */
export function snapshotStale(nowMs: number, live: boolean, updatedAtMs: number): boolean {
  if (live || updatedAtMs === 0) return false;
  return nowMs - updatedAtMs > SNAPSHOT_STALE_UI_MS;
}

/**
 * Repeated quiet-refresh failures under a still-pinging stream mean the view
 * can no longer be healed in place — the stream must be torn down so the
 * dead-transport machinery (fallback poll, reconnect, stale-UI gate) takes
 * over. Without this, a failed refresh just re-arms and the frozen view stays
 * live-badged forever.
 */
export function quietRefreshEscalates(consecutiveFailures: number): boolean {
  return consecutiveFailures >= QUIET_REFRESH_FAILURES_TO_DISCONNECT;
}

/**
 * A live-badged view whose snapshot is old AND whose self-heal has actually
 * failed to pull owes the agent an "as of" watermark: that combination is a
 * frozen view, not a quiet one. Either signal alone is innocent — broadcasts
 * are change-driven, so age is what a healthy lull looks like, and a failure
 * streak resets the moment any fetch or real event lands. Dead streams
 * (`live` false) have the harder stale-UI gate instead, and
 * `updatedAtMs === 0` is initial load.
 */
export function snapshotLagging(
  nowMs: number,
  live: boolean,
  updatedAtMs: number,
  quietRefreshFailures: number
): boolean {
  if (!live || updatedAtMs === 0 || quietRefreshFailures < 1) return false;
  return nowMs - updatedAtMs > SNAPSHOT_WATERMARK_MS;
}

/**
 * Whether this quiet-refresh failure is worth a telemetry event: the first
 * failure of an episode always is (the counter resets on any success or real
 * pool event), then a sustained outage reports on a fixed cadence rather than
 * once per failed refresh.
 */
export function shouldReportQuietRefreshFailure(
  nowMs: number,
  consecutiveFailures: number,
  lastReportedAtMs: number
): boolean {
  if (consecutiveFailures <= 1) return true;
  return nowMs - lastReportedAtMs >= QUIET_REFRESH_REPORT_MIN_INTERVAL_MS;
}
